/**
 * Session Config Service for Azure OpenAI Realtime API
 * 
 * This service handles communication with the backend to obtain
 * session configuration for the Azure OpenAI Realtime WebSocket API.
 * Also handles pre-establishing WebSocket connections for instant streaming.
 */

import { SessionConfigResponse, TokenResponse } from '../types';
import { GenAILiveClient } from './genai-live-client';
import { config } from '../config/runtime-config';

/**
 * Renewal result data for token context
 */
export interface RenewalResultData {
  error?: boolean;
  RequestId?: number;
  RequestDate?: string;
  Message?: string;
  URLMessage?: string;
}

/**
 * Search results data for lost report flow
 */
export interface SearchResultsData {
  count: number;
  report_number?: string;
  report_date?: string;
  plate_number?: string;
  plate_color?: string;
  plate_source?: string;
  plate_category?: string;
}

/**
 * Accident result data
 */
export interface AccidentResultData {
  success: boolean;
  report_number?: string;
  message?: string;
}

/**
 * Context data passed to backend for system instruction injection
 * This preserves conversation state across agent transitions
 */
export interface TokenContextData {
  // Core context - ALL flows
  language?: 'ar' | 'en' | 'ur';
  conversation_summary?: string;
  user_name?: string;
  user_mobile?: string;
  user_location?: string;
  
  // Renewal flow data
  renewal_result?: RenewalResultData;
  report_data?: {
    vehicle_count?: number;
  };
  selected_vehicle?: {
    plate_number?: string;
    plate_category?: string;
  };
  
  // Lost Report flow data
  search_results?: SearchResultsData;
  
  // Accident flow data
  accident_result?: AccidentResultData;
  image_analysis?: string;  // Image analysis from accident photos
  
  // FAQ flow
  pending_question?: string;
}

/**
 * Request parameters for fetching a Gemini token
 */
export interface TokenRequest {
  agent_id: string;
  voice_gender?: 'male' | 'female';
  context?: TokenContextData;
}

/**
 * Backend URL configuration
 * Loaded from config.js (runtime) or .env (build-time)
 */
const BACKEND_URL = config.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
  throw new Error('REACT_APP_BACKEND_URL is required in config.js file');
}

/**
 * Get the backend URL from configuration
 */
export function getBackendUrl(): string {
  if (!BACKEND_URL) {
    throw new Error('REACT_APP_BACKEND_URL is required in config.js file');
  }
  console.log(`🔗 [TOKEN-SERVICE] Using backend URL: ${BACKEND_URL}`);
  return BACKEND_URL;
}

/**
 * Fetch session configuration for a specific agent from the backend.
 * Returns the Azure OpenAI Realtime session config including
 * system instructions, tools, and voice settings.
 * 
 * @param backendUrl - The backend API URL
 * @param agentId - The agent ID to get config for
 * @param voiceGender - Voice gender preference ('male' or 'female')
 * @param context - Optional context data to inject into system instruction
 * @returns SessionConfigResponse with endpoint, deployment, session_config, and initial_prompt
 * @throws Error if fetch fails
 */
export async function fetchSessionConfig(
  backendUrl: string,
  agentId: string,
  voiceGender: 'male' | 'female' = 'male',
  context?: TokenContextData
): Promise<SessionConfigResponse> {
  console.log(`🔑 [SESSION-SERVICE] Fetching session config for agent: ${agentId}`);
  console.log(`🎤 [SESSION-SERVICE] Voice gender: ${voiceGender}`);
  if (context?.language) {
    console.log(`🌐 [SESSION-SERVICE] Language context: ${context.language}`);
  }

  const requestBody: TokenRequest = {
    agent_id: agentId,
    voice_gender: voiceGender,
    context: context,
  };

  try {
    const response = await fetch(`${backendUrl}/api/get-session-config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [SESSION-SERVICE] Config fetch failed: ${response.status} ${errorText}`);
      throw new Error(`Failed to fetch session config: ${response.status} ${errorText}`);
    }

    const data: SessionConfigResponse = await response.json();
    
    console.log(`✅ [SESSION-SERVICE] Config fetched for agent: ${data.agent_id}`);
    console.log(`📋 [SESSION-SERVICE] Deployment: ${data.deployment}`);
    
    return data;
  } catch (error) {
    console.error(`❌ [SESSION-SERVICE] Error fetching session config:`, error);
    throw error;
  }
}

/**
 * Backwards compatibility alias - wraps fetchSessionConfig to return TokenResponse shape
 */
export async function fetchGeminiToken(
  backendUrl: string,
  agentId: string,
  voiceGender: 'male' | 'female' = 'male',
  context?: TokenContextData
): Promise<TokenResponse> {
  const sessionConfig = await fetchSessionConfig(backendUrl, agentId, voiceGender, context);
  
  // Return as TokenResponse shape for backward compatibility
  return {
    token: sessionConfig.endpoint,
    model: sessionConfig.deployment,
    agent_id: sessionConfig.agent_id,
    initial_prompt: sessionConfig.initial_prompt,
    // Attach the full session config as extra data
    ...(sessionConfig as any),
  };
}

/**
 * Check if the backend is available
 * 
 * @param backendUrl - The backend API URL
 * @returns true if backend is available, false otherwise
 */
export async function checkBackendHealth(backendUrl: string): Promise<boolean> {
  try {
    const response = await fetch(`${backendUrl}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return response.ok;
  } catch (error) {
    console.error(`❌ [TOKEN-SERVICE] Backend health check failed:`, error);
    return false;
  }
}

/**
 * Get list of available agents from backend
 * 
 * @param backendUrl - The backend API URL
 * @returns Array of agent IDs
 */
export async function getAvailableAgents(backendUrl: string): Promise<string[]> {
  try {
    const response = await fetch(`${backendUrl}/api/agents`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch agents: ${response.status}`);
    }
    
    const data = await response.json();
    return data.agents || [];
  } catch (error) {
    console.error(`❌ [TOKEN-SERVICE] Error fetching agents:`, error);
    return [];
  }
}

/**
 * Pre-connected WebSocket client cache for instant streaming
 */
let cachedWebSocketClient: GenAILiveClient | null = null;
let cachedWebSocketClientExpiry: number = 0;
let cachedWebSocketModel: string | null = null;
const CONNECTION_CACHE_DURATION = 45000; // 45 seconds

/**
 * Pre-establishes WebSocket connection for greeting_agent
 * This eliminates ALL connection latency for instant user experience
 * 
 * @param backendUrl - The backend API URL
 * @param voiceGender - Voice gender preference ('male' or 'female')
 */
export async function prefetchInitialConnection(
  backendUrl: string,
  voiceGender: 'male' | 'female' = 'male'
): Promise<void> {
  try {
    console.log('🚀 [SESSION-SERVICE] Pre-establishing WebSocket connection for greeting_agent...');
    
    // Clean up any existing cached connection
    if (cachedWebSocketClient) {
      console.log('🧹 [SESSION-SERVICE] Cleaning up previous cached connection');
      cachedWebSocketClient.disconnect();
      cachedWebSocketClient = null;
    }
    
    // Fetch session config
    const configData = await fetchSessionConfig(backendUrl, 'greeting_agent', voiceGender);
    
    console.log('🔌 [SESSION-SERVICE] Establishing WebSocket connection...');
    
    // Create client with Azure OpenAI config (no API key needed for proxy mode)
    const client = new GenAILiveClient({
      endpoint: configData.endpoint,
      deployment: configData.deployment,
      apiVersion: configData.api_version,
    });
    
    // Establish WebSocket connection via backend proxy (key stays server-side)
    const connected = await client.connectViaProxy(backendUrl, 'greeting_agent', voiceGender);
    
    if (!connected) {
      console.error('❌ [SESSION-SERVICE] WebSocket connection failed');
      client.disconnect();
      return;
    }
    
    // Cache the connected client
    cachedWebSocketClient = client;
    cachedWebSocketClientExpiry = Date.now() + CONNECTION_CACHE_DURATION;
    cachedWebSocketModel = configData.deployment;
    
    console.log('✅ [SESSION-SERVICE] WebSocket pre-connected and ready for instant streaming!');
  } catch (error) {
    console.error('❌ [SESSION-SERVICE] Failed to pre-establish connection:', error);
    if (cachedWebSocketClient) {
      cachedWebSocketClient.disconnect();
      cachedWebSocketClient = null;
    }
  }
}

/**
 * Gets the pre-connected WebSocket client (one-time use)
 * Returns null if cache is expired or not available
 * 
 * @returns Object with client and model, or null
 */
export function getCachedWebSocketClient(): { client: GenAILiveClient; model: string } | null {
  if (!cachedWebSocketClient || !cachedWebSocketModel || Date.now() >= cachedWebSocketClientExpiry) {
    if (cachedWebSocketClient) {
      console.log('⏰ [TOKEN-SERVICE] Cached WebSocket expired, disconnecting');
      cachedWebSocketClient.disconnect();
    }
    cachedWebSocketClient = null;
    cachedWebSocketModel = null;
    return null;
  }
  
  console.log('⚡ [TOKEN-SERVICE] Using pre-connected WebSocket for INSTANT streaming!');
  const result = { client: cachedWebSocketClient, model: cachedWebSocketModel };
  cachedWebSocketClient = null; // Use once
  cachedWebSocketModel = null;
  return result;
}

